import { useCallback, useContext, useEffect, useState } from 'react';
import { DataTable, DataTableColumn, DataTableRow } from '../../../components/table/data-table';
import { Typography } from '../../../components/typography/typography';
import { QuipliLink } from '../../../components/link/quipli-link';
import { debounce } from 'lodash';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { ApiError } from '../../../clients/axios-instance';
import { Admin } from '../../../clients/quipli-service/admin/admin';
import { TenantContext } from '../../../context/tenant-context';
import { Modal } from '../../../components/modal/modal';
import { AdminUserForm } from '../components/admin-user-form';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import classNames from 'classnames';
import { useAdmin } from '../../../hooks/use-admin-context';
import { Dropdown } from '../../../components/dropdown/dropdown';
import { toast } from '../../../util/toasts';
import { useAdminApiV1 } from '../../../clients/quipli-service/admin/admin-api-v1';
import { useNavigate } from 'react-router-dom';

export const AdminUsersList = () => {
  const { getAdminsList, deleteAdmin } = useAdminApiV1();
  const adminLogged = useAdmin();
  const [isTableDataLoading, setIsTableDataLoading] = useState<boolean>(true),
    [tableData, setTableData] = useState<DataTableRow[]>([]),
    [isAddModalOpen, openAddModal] = useState<boolean>(false),
    [isModalDeleteOpen, setModalDeleteOpen] = useState<boolean>(false),
    [deleteId, setDeleteId] = useState<number>(0);
  const navigate = useNavigate();

  const columns: DataTableColumn[] = [
    { key: 'name', label: 'Name', showRule: 'OnlyDesktop' },
    { key: 'userName', label: 'User name', showRule: 'OnlyDesktop' },
    { key: 'email', label: 'Email', showRule: 'OnlyDesktop' },
    { key: 'superAdmin', label: 'Super admin', showRule: 'OnlyDesktop' },
    { key: 'actions', label: '', fixedAsLast: true, showRule: 'OnlyDesktop' },
  ];

  const getUserList = useCallback(
    debounce(function () {
      setIsTableDataLoading(true);
      getAdminsList()
        .then((items) => {
          setTableData(items.map(buildDataRow));
          setIsTableDataLoading(false);
        })
        .catch(() => {
          setIsTableDataLoading(false);
          setTableData([]);
        });
    }, 600),
    [],
  );

  const buildDataRow = (admin: Admin) => {
    return {
      mobileHeader: (
        <>
          <Typography type="text-md" weight="medium" className="text-quipli-black-800">
            {admin.adminIsSuperAdmin ? (
              admin.adminFullName
            ) : (
              <QuipliLink
                to={
                  admin.adminId === adminLogged.id
                    ? `admins/${admin.adminId}/permissions`
                    : `admins/${admin.adminId}/profile`
                }
                className={classNames('underline text-[var(--quipli-black-800)]')}
              >
                {admin.adminFullName}
              </QuipliLink>
            )}
          </Typography>
          <Typography type="text-sm" weight="regular" className="inline text-[var(--grey-500)]">
            {admin.adminEmail}
          </Typography>
        </>
      ),
      mobileHeaderAction: <Actions adminUser={admin} deleteUser={deleteModal} />,
      fields: {
        name: (
          <Typography type="text-sm" className="text-quipli-black-800">
            {admin.adminFullName}
          </Typography>
        ),

        userName: (
          <Typography type="text-sm" className="text-quipli-black-800">
            {admin.adminUsername}
          </Typography>
        ),
        email: (
          <Typography type="text-sm" className="text-quipli-black-800">
            {admin.adminEmail}
          </Typography>
        ),
        superAdmin: (
          <Typography type="text-sm" className="text-quipli-black-800">
            {admin.adminIsSuperAdmin ? 'Yes' : 'No'}
          </Typography>
        ),
        actions: <Actions adminUser={admin} deleteUser={deleteModal} />,
      },
      redirectTo:
        admin.adminId === adminLogged.id ? `admins/${admin.adminId}/permissions` : `admins/${admin.adminId}/profile`,
    };
  };

  const deleteModal = async (admin: Admin) => {
    setDeleteId(admin.adminId);
    setModalDeleteOpen(true);
  };

  const deleteAdminUser = async () => {
    if (deleteId == 0) {
      return;
    }

    deleteAdmin(deleteId)
      .then(() => {
        toast('User removed successfully', 'success');
        getUserList();
      })
      .catch((err: ApiError) => {
        const errMsg = err.status === 400 && err.message ? err.message : 'Something is wrong please try again.';
        toast(errMsg, 'error');
      });

    setModalDeleteOpen(false);
    setDeleteId(0);
  };

  const closeAddModal = () => {
    openAddModal(false);
  };

  const setNewAdmin = () => {
    getUserList();
  };

  useEffect(() => {
    getUserList();
  }, []);

  return (
    <>
      <div className="admin-users-list">
        <div className="container-v2 relative p-2">
          <div className="pb-5 flex justify-between items-center">
            <Typography type="display-sm" weight="medium">
              Manage Admin Users
            </Typography>
            <div className="flex flex-row gap-2">
              {adminLogged.hasPermissions('General Settings') && (
                <Button
                  className="button-secondary !border-0 h-[46px]"
                  data-cy="add-admin"
                  onClick={() => navigate('passwordRequirements')}
                >
                  <Typography
                    type="text-md"
                    weight="semi-bold"
                    className="flex justify-center justify-items-center gap-1 text-grey-700"
                  >
                    Manage Password Requirements
                  </Typography>
                </Button>
              )}
              <Button
                className="button-yellow !border-0 h-[46px]"
                data-cy="add-admin"
                onClick={() => openAddModal(true)}
              >
                <Typography
                  type="text-md"
                  weight="semi-bold"
                  className="flex justify-center justify-items-center gap-1 text-grey-700"
                >
                  <Icon icon="Plus" stroke="var(--grey-700)" />
                  Add User
                </Typography>
              </Button>
            </div>
          </div>

          <DataTable
            isLoading={isTableDataLoading}
            columnNames={columns}
            data={tableData}
            cypressPrevious="previous_renters_page"
            cypressNext="next_renters_page"
          />
        </div>
      </div>
      <Modal title={`Add new user`} isOpen={isAddModalOpen} close={closeAddModal}>
        <AdminUserForm className="" close={closeAddModal} setNewUser={setNewAdmin} />
      </Modal>
      <ConfirmModal
        isOpen={isModalDeleteOpen}
        close={() => {
          setModalDeleteOpen(false);
          setDeleteId(0);
        }}
        onConfirm={deleteAdminUser}
        headerIcon={
          <div className="flex justify-center">
            <Icon icon="XCircle" size="xlarge" stroke="var(--error-500)" />
          </div>
        }
        prompt="Remove user?"
      />
    </>
  );
};

const Actions = ({ adminUser, deleteUser }: { adminUser: Admin; deleteUser: (adminUser: Admin) => void }) => {
  const { contextPath } = useContext(TenantContext);
  const adminLogged = useAdmin();

  return (
    <div>
      {!adminUser.adminIsSuperAdmin ? (
        <Dropdown
          placement="left-end"
          trigger={() => (
            <Button className="button-tertiary" data-cy={`admin_menu`}>
              <Icon icon="DotsVertical" />
            </Button>
          )}
        >
          {adminLogged.isSuperAdmin ? (
            <div
              onClick={() => {
                const url = `${contextPath}manager/admin/login_as/${adminUser.adminId}`;
                window.open(url, '_self')?.focus();
              }}
              data-cy="login-as-admin"
            >
              <div className="flex gap-2">
                <Icon icon="LogIn04" />
                <Typography type="text-sm" className="text-quipli-black-800">
                  Login as Admin
                </Typography>
              </div>
            </div>
          ) : (
            ''
          )}

          {adminUser.adminId !== adminLogged.id ? (
            <div
              className="flex gap-2"
              onClick={() => {
                deleteUser(adminUser);
              }}
              data-cy="delete-admin"
            >
              <Icon icon="Trash" stroke="var(--grey-500)" />
              <Typography type="text-sm" className="text-quipli-black-800">
                Remove User
              </Typography>
            </div>
          ) : (
            ''
          )}
        </Dropdown>
      ) : (
        ''
      )}
    </div>
  );
};
